// 특정 날짜·기간의 슬롯 일괄 생성 모달 (관리자·강사 공용) — 명세 5.2·5.3.
// "매주 반복"은 가용시간 규칙(AvailabilityRulesSection)이 담당하고, 이 모달은
// 날짜가 정해진 두 의도만 다룬다 (사용자 어휘 기준으로 진입점 분리, 2026-07-27):
//  - intent='dated'   특정 날짜(하루~짧은 기간)에 예약 시간 추가 — 즉시 공개 기본
//  - intent='prebook' 접수제(사전예약형) 프로그램의 슬롯 준비 — 작성중 생성 후
//                     검토·일괄 공개, 오픈기간과 세트 (관리자 전용 진입)
// 타임블럭 템플릿(요일·시간창 프리셋)을 공유하고, 슬롯 단위는 프로그램 소관.
// 운영 시간대는 여러 개 가능 (2026-10 — 예: 주말 12:00~12:40 + 17:00~17:40을 한 번에.
// 전엔 시간대마다 날짜·요일·강사를 다시 입력해 따로 생성해야 했다).
// lockEducatorId: 강사 모드 — 본인 고정, 배정된 프로그램만.

import { useEffect, useMemo, useState } from 'react'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import ModalShell from '../../components/common/ModalShell.jsx'
import TimeField from '../../components/common/TimeField.jsx'
import { todayStr } from '../../utils/dateUtils.js'
import { makeId } from '../../context/dataModel.js'
import { addDaysStr } from '../bookingRules.js'
import { generateSlotsDetailed } from '../slotGeneration.js'
import { useBooking } from '../BookingContext.jsx'
import { bookingMessage } from '../bookingMessages.js'
import SlotConflictList from './SlotConflictList.jsx'
import SlotEditorModal from './SlotEditorModal.jsx'

const FIELD = 'h-10 px-3 rounded-lg border border-gray-200 text-sm'
const WEEKDAYS = [
  { value: 1, label: '월' }, { value: 2, label: '화' }, { value: 3, label: '수' },
  { value: 4, label: '목' }, { value: 5, label: '금' }, { value: 6, label: '토' },
  { value: 0, label: '일' },
]

// 템플릿의 운영 시간대 — 시간대 여러 개가 도입되기 전(2026-10) 템플릿은 dayStart/dayEnd 하나
function templateRanges(t) {
  return t.ranges?.length ? t.ranges.map((r) => ({ ...r })) : [{ start: t.dayStart, end: t.dayEnd }]
}

export default function TimetableWizard({
  onClose, lockEducatorId = null, intent = 'dated', lockProgramId = null,
}) {
  const {
    config, userNames, slots, reservations, createSlotBatch, saveTimetableTemplates, actor, deleteSlots,
  } = useBooking()
  const isAdmin = actor.role === 'admin'
  const templates = config.templates ?? []
  const isPrebook = intent === 'prebook'

  // 강사 모드: 배정된 프로그램만 / 접수제 모드: 사전예약형 프로그램만
  // lockProgramId: 프로그램 카드에서 진입 — 그 프로그램으로 고정
  const programs = useMemo(() => {
    let list = config.programs.filter((p) => p.active)
    if (lockProgramId) return list.filter((p) => p.id === lockProgramId)
    if (isPrebook) list = list.filter((p) => p.requiresOpenPeriod)
    if (!lockEducatorId) return list
    const mine = new Set(
      config.educators
        .filter((e) => e.educatorId === lockEducatorId && e.active)
        .map((e) => e.programId),
    )
    return list.filter((p) => mine.has(p.id))
  }, [config.programs, config.educators, lockEducatorId, isPrebook, lockProgramId])

  const [form, setForm] = useState({
    programId: programs[0]?.id ?? '',
    educatorId: lockEducatorId ?? '',
    subjectId: '',
    from: todayStr(),
    // dated: 하루가 기본 (기간으로 늘릴 수 있음) / prebook: 4주 배치가 기본
    to: isPrebook ? addDaysStr(todayStr(), 27) : todayStr(),
    weekdays: isPrebook ? [1, 2, 3, 4, 5] : [0, 1, 2, 3, 4, 5, 6],
    ranges: [{ start: '16:00', end: '21:00' }], // 일별 운영 시간대 (1개 이상)
    capacity: null,
    breakStart: '',
    breakEnd: '',
    excludeDates: '', // 휴무일 (쉼표 구분, 명세 5.2)
    publishNow: !isPrebook, // dated는 즉시 공개, prebook은 검토 후 공개가 기본
  })
  const [appliedTemplateId, setAppliedTemplateId] = useState(null)
  const [savingTemplate, setSavingTemplate] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const program = programs.find((p) => p.id === form.programId)
  const educatorOptions = config.educators.filter((e) => e.programId === form.programId && e.active)
  const subjectOptions = config.subjects.filter((s) => s.programId === form.programId && s.active)

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
  const setTime = (key) => (v) => setForm((f) => ({ ...f, [key]: v }))
  const setRange = (i, key) => (v) => {
    setAppliedTemplateId(null)
    setForm((f) => ({ ...f, ranges: f.ranges.map((r, j) => (j === i ? { ...r, [key]: v } : r)) }))
  }
  const addRange = () => {
    setAppliedTemplateId(null)
    setForm((f) => ({ ...f, ranges: [...f.ranges, { start: '', end: '' }] }))
  }
  const removeRange = (i) => {
    setAppliedTemplateId(null)
    setForm((f) => ({ ...f, ranges: f.ranges.filter((_, j) => j !== i) }))
  }
  const toggleDay = (d) => {
    setAppliedTemplateId(null)
    setForm((f) => ({
      ...f,
      weekdays: f.weekdays.includes(d) ? f.weekdays.filter((x) => x !== d) : [...f.weekdays, d],
    }))
  }

  const applyTemplate = (t) => {
    setAppliedTemplateId(t.id)
    setForm((f) => ({
      ...f,
      weekdays: [...t.weekdays],
      ranges: templateRanges(t),
      breakStart: t.breakStart ?? '',
      breakEnd: t.breakEnd ?? '',
    }))
  }

  const handleSaveTemplate = async () => {
    const name = templateName.trim()
    if (!name || busy) return
    setBusy(true)
    setError(null)
    try {
      const next = [...templates, {
        id: makeId('tpl'),
        name,
        weekdays: [...form.weekdays],
        // dayStart/dayEnd = 첫 시간대 — 가용시간 규칙(AvailabilityRulesSection)은
        // 시간대 하나만 쓰므로 하위 호환용으로 유지한다
        dayStart: form.ranges[0].start,
        dayEnd: form.ranges[0].end,
        ranges: form.ranges.map((r) => ({ ...r })),
        breakStart: form.breakStart,
        breakEnd: form.breakEnd,
      }]
      await saveTimetableTemplates(next)
      setTemplateName('')
      setSavingTemplate(false)
    } catch {
      setError('템플릿 저장에 실패했습니다.')
    } finally {
      setBusy(false)
    }
  }

  const handleDeleteTemplate = async (t) => {
    if (busy) return
    if (!window.confirm(`템플릿 "${t.name}"을 삭제할까요? 이미 생성된 슬롯에는 영향이 없습니다.`)) return
    setBusy(true)
    setError(null)
    try {
      await saveTimetableTemplates(templates.filter((x) => x.id !== t.id))
      if (appliedTemplateId === t.id) setAppliedTemplateId(null)
    } catch {
      setError('템플릿 삭제에 실패했습니다.')
    } finally {
      setBusy(false)
    }
  }

  const excludeDates = useMemo(
    () => form.excludeDates.split(',').map((s) => s.trim()).filter((s) => /^\d{4}-\d{2}-\d{2}$/.test(s)),
    [form.excludeDates],
  )

  // 하루짜리는 요일 필터 무시 (generateSlots에서 빈 배열 = 전 요일)
  const effectiveWeekdays = useMemo(
    () => (form.from === form.to ? [] : form.weekdays),
    [form.from, form.to, form.weekdays],
  )

  // 담당 강사의 기존 슬롯(강사지정예약 포함, 취소 제외)과 겹치는 시간은 만들지
  // 않는다 — SQL 규칙 파생(_booking_generate_rule_slots)과 같은 의미론 (2026-07-30)
  const blocked = useMemo(
    () => (form.educatorId
      ? slots.filter((s) => s.educatorId === form.educatorId && s.status !== 'cancelled')
      : []),
    [slots, form.educatorId],
  )

  // 미리보기 + 겹침으로 제외된 후보(skipped)를 한 번에 계산 (2026-09 — 겹침을
  // 눈에 보이게 하고 그 자리에서 해소하기 위해 skipped를 별도로 보존한다)
  const detailed = useMemo(() => {
    if (!program) return { slots: [], skipped: [] }
    return generateSlotsDetailed({
      from: form.from,
      to: form.to,
      weekdays: effectiveWeekdays,
      ranges: form.ranges,
      slotMinutes: program.slotMinutes,
      capacity: Number(form.capacity) || program.defaultCapacity,
      educatorId: form.educatorId || null,
      subjectId: form.subjectId || null,
      breaks: form.breakStart && form.breakEnd
        ? [{ start: form.breakStart, end: form.breakEnd }]
        : [],
      excludeDates,
      blocked,
    })
  }, [program, form, excludeDates, effectiveWeekdays, blocked])

  const preview = detailed.slots
  const previewDays = useMemo(() => new Set(preview.map((s) => s.date)).size, [preview])
  const blockedCount = detailed.skipped.length

  // 겹침 원인이 된 기존 슬롯 — 고유 슬롯만 (여러 후보가 같은 슬롯 하나에 걸릴 수 있다)
  const conflictSlots = useMemo(() => {
    const byId = new Map()
    for (const cand of detailed.skipped) {
      for (const b of cand.blockers) byId.set(b.id, b)
    }
    return [...byId.values()]
  }, [detailed.skipped])

  const conflictWithReservation = useMemo(
    () => conflictSlots.filter((s) => reservations.some((r) => r.slotId === s.id && r.status === 'confirmed')),
    [conflictSlots, reservations],
  )
  const conflictWithoutReservation = useMemo(
    () => conflictSlots.filter((s) => !conflictWithReservation.includes(s)),
    [conflictSlots, conflictWithReservation],
  )

  const [showConflicts, setShowConflicts] = useState(false)
  const [resolving, setResolving] = useState(false)
  const [resolveError, setResolveError] = useState(null)
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false)
  const [editingConflict, setEditingConflict] = useState(null)

  // 생성될 슬롯이 없고 겹침이 원인이면 막다른 길이 되지 않게 자동으로 펼친다
  const deadEnd = preview.length === 0 && blockedCount > 0
  useEffect(() => {
    if (deadEnd) setShowConflicts(true)
  }, [deadEnd])

  // 확정 예약이 없는 겹침 슬롯을 일괄 삭제 — booking_delete_slots RPC 한 번 호출
  // (add-booking-bulk-delete.sql). afterWrite가 refetch를 하므로 별도 refetch 불필요.
  const resolveConflicts = async () => {
    if (resolving || conflictWithoutReservation.length === 0) return
    setResolving(true)
    setResolveError(null)
    try {
      const result = await deleteSlots({
        slotIds: conflictWithoutReservation.map((s) => s.id),
        reason: '겹침 해소(위저드 일괄 삭제)',
      })
      if (!result?.ok) {
        setResolveError(bookingMessage(result?.code ?? 'ERROR'))
      } else if ((result.skipped ?? []).length > 0) {
        setResolveError(`${result.skipped.length}개는 삭제되지 않았습니다.`)
      }
    } finally {
      setResolving(false)
      setConfirmingBulkDelete(false)
    }
  }

  const submit = async () => {
    if (!program || busy) return
    if (preview.length === 0) {
      setError('생성될 슬롯이 없습니다. 입력값을 확인해 주세요.')
      return
    }
    setBusy(true)
    try {
      await createSlotBatch({
        programId: program.id,
        status: form.publishNow ? 'open' : 'draft',
        params: {
          from: form.from, to: form.to, weekdays: effectiveWeekdays,
          day_start: form.ranges[0].start, day_end: form.ranges[0].end,
          ranges: form.ranges,
          slot_minutes: program.slotMinutes,
          educator_id: form.educatorId || null,
          subject_id: form.subjectId || null,
          exclude_dates: excludeDates,
          template_id: appliedTemplateId,
        },
        slots: preview,
      })
      onClose()
    } catch {
      setError('저장에 실패했습니다. 다시 시도해 주세요.')
    } finally {
      setBusy(false)
    }
  }

  const title = isPrebook ? '접수제 슬롯 준비' : '특정 날짜 예약시간 추가'

  if (programs.length === 0) {
    return (
      <ModalShell title={title} onClose={onClose} maxWidth="max-w-xl">
        <p className="py-8 text-center text-sm text-gray-400">
          {isPrebook
            ? '접수제(사전예약형) 프로그램이 없습니다. 프로그램 설정에서 사전예약을 켜 주세요.'
            : '배정된 프로그램이 없습니다. 관리자에게 프로그램 배정을 요청해 주세요.'}
        </p>
      </ModalShell>
    )
  }

  return (
    <ModalShell title={title} onClose={onClose} maxWidth="max-w-xl">
      {isPrebook && (
        <p className="rounded-xl bg-blue-50 p-3 text-xs text-blue-700">
          접수제 슬롯은 <b>작성중</b>으로 만들어 검토한 뒤, 슬롯 목록에서 접수 시작에 맞춰
          일괄 <b>예약공개</b>로 전환합니다. 접수기간은 프로그램 메뉴의 예약 오픈기간에서 등록하세요.
        </p>
      )}
      {/* 타임블럭 템플릿 — 요일·운영시간·휴식 프리셋 */}
      <div>
        <p className="text-xs text-gray-500 mb-1">타임블럭 템플릿 (클릭하면 요일·시간이 채워집니다)</p>
        <div className="flex flex-wrap gap-1.5">
          {templates.map((t) => (
            <span
              key={t.id}
              className={`inline-flex items-center rounded-full border text-[11px] font-bold overflow-hidden ${
                appliedTemplateId === t.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-600 border-gray-200'
              }`}
            >
              <button
                type="button"
                onClick={() => applyTemplate(t)}
                className="px-2.5 h-7"
                title={templateRanges(t).map((r) => `${r.start}~${r.end}`).join(', ')}
              >
                {t.name}
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => handleDeleteTemplate(t)}
                  className={`pr-2 ${appliedTemplateId === t.id ? 'text-blue-200' : 'text-gray-300'} hover:text-red-500`}
                  aria-label={`템플릿 ${t.name} 삭제`}
                >
                  <X size={12} />
                </button>
              )}
            </span>
          ))}
          {isAdmin && !savingTemplate && (
            <button
              type="button"
              onClick={() => setSavingTemplate(true)}
              className="px-2.5 h-7 rounded-full border border-dashed border-blue-300 text-blue-600 text-[11px] font-bold"
            >
              + 현재 설정을 템플릿으로
            </button>
          )}
        </div>
        {isAdmin && savingTemplate && (
          <div className="flex gap-1.5 mt-1.5">
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="템플릿 이름 (예: C · 방학 오전형)"
              className={`${FIELD} flex-1`}
            />
            <button
              type="button"
              onClick={handleSaveTemplate}
              disabled={busy || !templateName.trim()}
              className="px-3 h-10 rounded-lg bg-blue-600 text-white text-xs font-bold disabled:opacity-50"
            >
              저장
            </button>
            <button
              type="button"
              onClick={() => setSavingTemplate(false)}
              className="px-3 h-10 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold"
            >
              취소
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {lockProgramId ? (
          <p className="text-xs text-gray-500 col-span-2 pb-1">
            프로그램: <b className="text-gray-700">{program?.name}</b> ({program?.slotMinutes}분 단위)
          </p>
        ) : (
          <label className="text-xs text-gray-500 col-span-2">
            프로그램
            <select value={form.programId} onChange={set('programId')} className={`${FIELD} w-full mt-1`}>
              {programs.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.slotMinutes}분)</option>)}
            </select>
          </label>
        )}
        {lockEducatorId ? (
          <p className="text-xs text-gray-500 self-end pb-2">
            담당 강사: <b className="text-gray-700">{userNames[lockEducatorId]?.name ?? '나'}</b> (본인)
          </p>
        ) : (
          <label className="text-xs text-gray-500">
            담당 강사
            <select value={form.educatorId} onChange={set('educatorId')} className={`${FIELD} w-full mt-1`}>
              <option value="">미지정</option>
              {educatorOptions.map((e) => (
                <option key={e.id} value={e.educatorId}>{userNames[e.educatorId]?.name ?? e.educatorId}</option>
              ))}
            </select>
          </label>
        )}
        {program?.usesSubject && (
          <label className="text-xs text-gray-500">
            교과
            <select value={form.subjectId} onChange={set('subjectId')} className={`${FIELD} w-full mt-1`}>
              <option value="">미지정</option>
              {subjectOptions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}
        <label className="text-xs text-gray-500">
          날짜 (시작)
          <input type="date" value={form.from} onChange={set('from')} className={`${FIELD} w-full mt-1`} />
        </label>
        <label className="text-xs text-gray-500">
          날짜 (종료 — 하루면 시작과 동일)
          <input type="date" value={form.to} onChange={set('to')} className={`${FIELD} w-full mt-1`} />
        </label>
        <div className="text-xs text-gray-500 col-span-2">
          운영 시간 (시작 ~ 종료)
          {form.ranges.map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 mt-1">
              <TimeField value={r.start} onChange={setRange(i, 'start')} className={`${FIELD} flex-1 min-w-0`} />
              <span className="text-gray-400">~</span>
              <TimeField value={r.end} onChange={setRange(i, 'end')} className={`${FIELD} flex-1 min-w-0`} />
              {form.ranges.length > 1 ? (
                <button
                  type="button"
                  onClick={() => removeRange(i)}
                  className="w-8 h-10 shrink-0 flex items-center justify-center text-gray-300 hover:text-red-500"
                  aria-label={`운영 시간대 ${i + 1} 삭제`}
                >
                  <X size={16} />
                </button>
              ) : null}
            </div>
          ))}
          <button
            type="button"
            onClick={addRange}
            className="mt-1.5 w-full h-9 rounded-lg border border-dashed border-blue-300 text-blue-600 text-xs font-bold"
          >
            + 운영 시간대 추가 (같은 날짜·요일·강사로 다른 시간에도 생성)
          </button>
        </div>
        <label className="text-xs text-gray-500">
          휴식 시작 (선택)
          <TimeField value={form.breakStart} onChange={setTime('breakStart')} className={`${FIELD} w-full mt-1`} />
        </label>
        <label className="text-xs text-gray-500">
          휴식 종료 (선택)
          <TimeField value={form.breakEnd} onChange={setTime('breakEnd')} className={`${FIELD} w-full mt-1`} />
        </label>
        <label className="text-xs text-gray-500">
          슬롯 정원 (기본 {program?.defaultCapacity ?? 1})
          <input type="number" min="1" value={form.capacity ?? ''} onChange={set('capacity')} className={`${FIELD} w-full mt-1`} />
        </label>
        <label className="text-xs text-gray-500 col-span-2">
          휴무일 (선택 — 쉼표로 구분)
          <input
            type="text"
            value={form.excludeDates}
            onChange={set('excludeDates')}
            placeholder="예: 2026-07-30, 2026-08-14"
            className={`${FIELD} w-full mt-1`}
          />
        </label>
      </div>

      {/* 하루짜리 dated에는 요일 선택이 무의미 — 기간일 때만 노출 */}
      {form.from !== form.to && (
        <div>
          <p className="text-xs text-gray-500 mb-1">기간 중 운영 요일</p>
          <div className="flex gap-1.5">
            {WEEKDAYS.map((d) => (
              <button
                key={d.value}
                type="button"
                onClick={() => toggleDay(d.value)}
                className={`w-9 h-9 rounded-lg text-xs font-bold border ${
                  form.weekdays.includes(d.value)
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-500 border-gray-200'
                }`}
              >
                {d.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* 접수제는 항상 작성중 생성 (검토 → 접수 시작에 일괄 공개) — 선택지 자체를 숨긴다 */}
      {!isPrebook && (
        <label className="flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            checked={form.publishNow}
            onChange={(e) => setForm((f) => ({ ...f, publishNow: e.target.checked }))}
          />
          생성 즉시 예약공개 (미체크 시 작성중으로 생성 — 검토 후 공개)
        </label>
      )}

      <div className="rounded-xl bg-gray-50 p-3 text-xs text-gray-600">
        미리보기: <b>{previewDays}일 × 슬롯 {preview.length}개</b>가{' '}
        <b>{form.publishNow ? '예약공개' : '작성중'}</b> 상태로 생성됩니다.
        {blockedCount > 0 && (
          <>
            <span className="block mt-1 text-orange-500">
              강사의 기존 슬롯·지정 예약과 겹치는 {blockedCount}개는 만들지 않습니다.
              {' '}
              <button
                type="button"
                onClick={() => setShowConflicts((v) => !v)}
                className="inline-flex items-center underline font-bold"
              >
                겹치는 슬롯 보기 ({conflictSlots.length}개)
                {showConflicts ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              </button>
            </span>
            {deadEnd && (
              <p className="mt-1.5 rounded-lg bg-orange-50 text-orange-600 p-2 font-bold">
                이 시간에 이미 슬롯이 있어 만들 수 없습니다. 아래 기존 슬롯을 삭제하거나
                시간을 바꿔 주세요.
              </p>
            )}
          </>
        )}
        {preview.length > 0 && (
          <span className="block mt-1 text-gray-400">
            예: {preview[0].date} {preview[0].startTime}~{preview[0].endTime} ...
          </span>
        )}
      </div>

      {showConflicts && blockedCount > 0 && (
        <div className="space-y-2">
          <SlotConflictList blockers={conflictSlots} onEdit={setEditingConflict} />

          {conflictWithoutReservation.length > 0 && (
            <div className="rounded-xl bg-white border border-gray-200 p-3 space-y-2">
              {!confirmingBulkDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmingBulkDelete(true)}
                  disabled={resolving}
                  className="w-full h-10 rounded-lg bg-red-50 text-red-600 text-xs font-bold disabled:opacity-50"
                >
                  예약 없는 겹침 슬롯 {conflictWithoutReservation.length}개 삭제
                </button>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-xs text-gray-600">
                    예약 없는 겹침 슬롯 {conflictWithoutReservation.length}개를 삭제할까요?
                    삭제 후 미리보기가 다시 계산됩니다.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setConfirmingBulkDelete(false)}
                      disabled={resolving}
                      className="flex-1 h-9 rounded-lg bg-gray-100 text-gray-600 text-xs font-bold disabled:opacity-50"
                    >
                      취소
                    </button>
                    <button
                      type="button"
                      onClick={resolveConflicts}
                      disabled={resolving}
                      className="flex-1 h-9 rounded-lg bg-red-500 text-white text-xs font-bold disabled:opacity-50"
                    >
                      {resolving ? '삭제 중...' : '삭제 확정'}
                    </button>
                  </div>
                </div>
              )}
              {resolveError && <p className="text-xs text-red-500">{resolveError}</p>}
            </div>
          )}

          {conflictWithReservation.length > 0 && (
            <p className="text-[11px] text-gray-500">
              예약이 있는 {conflictWithReservation.length}개는 개별 편집에서 처리해 주세요
              (위 목록의 [편집] 버튼).
            </p>
          )}
        </div>
      )}

      {!form.educatorId && (
        <p className="text-[11px] text-orange-500">
          담당 강사가 미지정입니다 — 생성 후 운영현황의 일정 이상 목록에 표시됩니다.
        </p>
      )}
      {error && <p className="text-xs text-red-500 bg-red-50 rounded-lg p-2">{error}</p>}

      <button
        type="button"
        onClick={submit}
        disabled={busy || preview.length === 0}
        className="w-full h-12 rounded-xl bg-blue-600 text-white font-bold disabled:opacity-50"
      >
        {busy ? '생성 중...' : `슬롯 ${preview.length}개 생성`}
      </button>

      {editingConflict && (
        <SlotEditorModal
          slot={editingConflict}
          program={config.programs.find((p) => p.id === editingConflict.programId)}
          isAdmin={isAdmin}
          onClose={() => setEditingConflict(null)}
        />
      )}
    </ModalShell>
  )
}
